import { formatDateTimeWithFormat, formatDayOfMonthWithYear } from "@clipboard-health/date-time";
import { DATE_TIME_FORMATS } from "@clipboard-health/date-time/src/lib/internal/constants";
import { Input, Text, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { DatePickerInputBottomSheet } from "@src/appV2/redesign/DateTime/DatePickerInputBottomSheet";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
import { differenceInDays, endOfDay, startOfDay, startOfYear } from "date-fns";
import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";

import { PageWrapper } from "../../components/PageWrapper";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { EARNINGS_FULL_PATH } from "../Earnings/paths";
import { useSendProofOfEarningsEmail } from "./api/useSendProofOfEarningsEmail";
import { ProofOfEarningsErrorBottomSheet } from "./components/ProofOfEarningsErrorBottomSheet";
import { ProofOfEarningsSendingBottomSheet } from "./components/ProofOfEarningsSendingBottomSheet";
import { ProofOfEarningsSuccessBottomSheet } from "./components/ProofOfEarningsSuccessBottomSheet";
import { type ProofOfEarningsFormValues, proofOfEarningsSchema } from "./schema";

export function ProofOfEarningsPage() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { formState, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      start: startOfYear(new Date()),
      end: endOfDay(new Date()),
    },
    resolver: zodResolver(proofOfEarningsSchema),
  });
  const [start, end] = watch(["start", "end"]);
  const sendProofOfEarningsEmail = useSendProofOfEarningsEmail();
  const worker = useDefinedWorker();

  const startDateModalState = useModalState();
  const endDateModalState = useModalState();

  const sendingEmailModalState = useModalState();
  const errorModalState = useModalState();
  const successModalState = useModalState();

  const handleStartDateConfirm = useCallback(
    (date: Date) => {
      setValue("start", startOfDay(date), { shouldValidate: true });
      startDateModalState.closeModal();
    },
    [setValue, startDateModalState]
  );

  const handleEndDateConfirm = useCallback(
    (date: Date) => {
      setValue("end", endOfDay(date), { shouldValidate: true });
      endDateModalState.closeModal();
    },
    [setValue, endDateModalState]
  );

  const sendEmail = async (data: ProofOfEarningsFormValues) => {
    sendingEmailModalState.openModal();

    try {
      const result = await sendProofOfEarningsEmail.mutateAsync({
        start: data.start,
        end: data.end,
      });

      if (!result.data?.success) {
        throw new Error("Could not send email.");
      }
    } catch {
      sendingEmailModalState.closeModal();
      errorModalState.openModal();
      return;
    }

    sendingEmailModalState.closeModal();
    successModalState.openModal();

    logEvent(USER_EVENTS.EMAIL_PAYSTUB, {
      startDate: formatDateTimeWithFormat(data.start, DATE_TIME_FORMATS.calendarDate),
      endDate: formatDateTimeWithFormat(data.end, DATE_TIME_FORMATS.calendarDate),
      dateRange: differenceInDays(data.end, data.start),
      userId: worker.userId,
    });
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          shouldWrapTitle
          title="Proof of Earnings"
          scrollContainerRef={scrollContainerRef}
          headerVariant="tertiary"
          fallbackGoBackPath={EARNINGS_FULL_PATH}
        />

        <PageContent ref={scrollContainerRef}>
          <Stack
            component="form"
            spacing={6}
            mt={5}
            direction="column"
            onSubmit={handleSubmit(sendEmail)}
          >
            <Stack spacing={2}>
              <Text semibold variant="h6">
                Start date
              </Text>
              <Input
                fullWidth
                readOnly
                placeholder="MM/DD/YYYY"
                value={formatDayOfMonthWithYear(start)}
                iconType="calendar-with-cells"
                error={Boolean(formState.errors.start)}
                onClick={startDateModalState.openModal}
              />
              {formState.errors.start && (
                <Text variant="caption" color="error">
                  {formState.errors.start.message}
                </Text>
              )}
            </Stack>

            <Stack spacing={2}>
              <Text semibold variant="h6">
                End date
              </Text>
              <Input
                fullWidth
                readOnly
                placeholder="MM/DD/YYYY"
                value={formatDayOfMonthWithYear(end)}
                iconType="calendar-with-cells"
                error={Boolean(formState.errors.end)}
                onClick={endDateModalState.openModal}
              />
              {formState.errors.end && (
                <Text variant="caption" color="error">
                  {formState.errors.end.message}
                </Text>
              )}
            </Stack>
            <Box pt={1}>
              <Button fullWidth type="submit" disabled={sendProofOfEarningsEmail.isLoading}>
                Email me my proof of earnings
              </Button>
            </Box>
          </Stack>
        </PageContent>

        <DatePickerInputBottomSheet
          disableFuture
          shouldAutoConfirmOnSelect
          shouldHideCloseButton
          modalState={startDateModalState}
          initialDate={start}
          maxDate={end}
          onConfirm={handleStartDateConfirm}
        />
        <DatePickerInputBottomSheet
          disableFuture
          shouldAutoConfirmOnSelect
          shouldHideCloseButton
          modalState={endDateModalState}
          initialDate={end}
          minDate={start}
          onConfirm={handleEndDateConfirm}
        />

        <ProofOfEarningsSendingBottomSheet modalState={sendingEmailModalState} />
        <ProofOfEarningsErrorBottomSheet
          isRetryLoading={sendProofOfEarningsEmail.isLoading}
          modalState={errorModalState}
          onRetry={() => {
            if (sendProofOfEarningsEmail.isLoading) {
              return;
            }
            errorModalState.closeModal();
            void handleSubmit(sendEmail)();
          }}
        />
        <ProofOfEarningsSuccessBottomSheet
          modalState={successModalState}
          workerEmail={worker.email}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
